import { useState, useCallback, useEffect, useRef } from "react"
import { GlassDrawer } from "@/components/ui/GlassDrawer"
import { GlassModal } from "@/components/ui/GlassModal"
import { useIsMobile } from "@/hooks/useMediaQuery"
import { useSupportFeedback } from "@/hooks/useSupportFeedback"
import { CategoryGrid } from "./CategoryGrid"
import { FeedbackForm } from "./FeedbackForm"
import { SuccessCard } from "./SuccessCard"
import type { FeedbackCategory, FeedbackTranscriptionSource } from "@/types/index"
import { mensagemParaUsuario } from "@/lib/erroParaUsuario"

type Step = "category" | "form" | "success" | "error"

interface SupportWidgetProps {
  open: boolean
  onClose: () => void
  initialScreenshot: Blob | null
  /** True enquanto a captura de tela em segundo plano (#192) ainda não
   *  terminou — o FeedbackForm mostra "Capturando..." nesse intervalo. */
  initialScreenshotPending?: boolean
}

interface SuccessState {
  referenceId: string
  // Esses 2 viram opcionais quando o backend processa em segundo plano:
  // o título e o link do ClickUp só ficam disponíveis após a IA + ClickUp
  // terminarem (alguns segundos depois). O usuário vê a confirmação na hora.
  uiTitle?: string | null
  clickupTaskUrl?: string | null
  warning?: string
}

export function SupportWidget({ open, onClose, initialScreenshot, initialScreenshotPending = false }: SupportWidgetProps) {
  const isMobile = useIsMobile()
  const { submit, isSubmitting } = useSupportFeedback()

  const [step, setStep] = useState<Step>("category")
  const [category, setCategory] = useState<FeedbackCategory | null>(null)
  const [success, setSuccess] = useState<SuccessState | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Reset state quando abre
  useEffect(() => {
    if (open) {
      setStep("category")
      setCategory(null)
      setSuccess(null)
      setSubmitError(null)
    }
  }, [open])

  const handleSelectCategory = (c: FeedbackCategory) => {
    setCategory(c)
    setStep("form")
  }

  const handleSubmit = useCallback(
    async (args: {
      message: string
      screenshotBlob: Blob | null
      transcriptionSource: FeedbackTranscriptionSource
    }) => {
      if (!category) return
      setSubmitError(null)
      try {
        const result = await submit({
          category,
          message: args.message,
          transcriptionSource: args.transcriptionSource,
          screenshotBlob: args.screenshotBlob,
        })
        setSuccess({
          referenceId: result.reference_id,
          uiTitle: result.ui_title,
          clickupTaskUrl: result.clickup_task_url,
          warning: result.warning,
        })
        setStep("success")
      } catch (err) {
        setSubmitError(
          mensagemParaUsuario(
            err,
            "Não foi possível enviar sua mensagem ao suporte. Tente de novo em instantes.",
            "SupportWidget",
          ),
        )
        setStep("error")
      }
    },
    [category, submit],
  )

  const handleSendAnother = () => {
    setStep("category")
    setCategory(null)
    setSuccess(null)
    setSubmitError(null)
  }

  const content = (
    <>
      {step === "category" && <CategoryGrid onSelect={handleSelectCategory} />}

      {step === "form" && category && (
        <FeedbackForm
          category={category}
          initialScreenshot={initialScreenshot}
          initialScreenshotPending={initialScreenshotPending}
          onBack={() => setStep("category")}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      )}

      {step === "success" && success && (
        <SuccessCard
          referenceId={success.referenceId}
          uiTitle={success.uiTitle}
          clickupTaskUrl={success.clickupTaskUrl}
          warning={success.warning}
          onSendAnother={handleSendAnother}
          onClose={onClose}
        />
      )}

      {step === "error" && (
        <div className="space-y-3 py-4 text-center">
          <p className="text-2xl">😕</p>
          <h3 className="text-base font-semibold text-[var(--text-primary)]">
            Não foi possível enviar
          </h3>
          <p className="text-sm text-[var(--text-secondary)]">
            {submitError || "Erro desconhecido. Tente novamente em alguns instantes."}
          </p>
          <button
            type="button"
            onClick={handleSendAnother}
            className="text-sm font-medium text-[var(--accent-blue)] hover:underline"
          >
            Tentar novamente
          </button>
        </div>
      )}
    </>
  )

  // Wrapper marcado pra ser escondido durante captura (retake).
  // Usamos um ref pra encontrar o portal-root [role="dialog"] após mount e
  // marcá-lo também — assim o painel inteiro do drawer/modal de suporte some
  // do print, mas outros dialogs (LeadDrawer, etc) ficam visíveis.
  const wrappedRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open || !wrappedRef.current) return
    const dialog = wrappedRef.current.closest('[role="dialog"]')
    if (dialog) {
      dialog.setAttribute("data-screenshot-hidden", "true")
      return () => dialog.removeAttribute("data-screenshot-hidden")
    }
  }, [open])

  const wrappedContent = (
    <div ref={wrappedRef} data-screenshot-hidden="true">
      {content}
    </div>
  )

  if (isMobile) {
    return (
      <GlassModal
        isOpen={open}
        onClose={onClose}
        title="Feedback rápido"
        size="md"
      >
        {wrappedContent}
      </GlassModal>
    )
  }

  return (
    <GlassDrawer
      isOpen={open}
      onClose={onClose}
      title="Feedback rápido"
      width="min(92vw, 460px)"
    >
      {wrappedContent}
    </GlassDrawer>
  )
}
